import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FEEDBACK_OPTIONS } from "../../lib/visitOptions";
import { todayIST, daysAgoIST } from "../../lib/datetime";
import { joinParts, NOT_CAPTURED } from "../../lib/vocabulary";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import CategoryBadge from "../../components/doctors/CategoryBadge";
import TerritoryUnitSelect from "../../components/common/TerritoryUnitSelect";
import {
  ArrowLeft,
  MapPin,
  Loader2,
  CheckCircle2,
  Plus,
  Trash2,
  Save,
} from "lucide-react";
import { toast } from "sonner";

// Who may file a DCR. Mirrors `routes_visits.VISIT_FILER_ROLES`; the server is
// the gate, this list only decides whether the form is worth drawing.
const FILER_ROLES = ["sales_officer", "assistant_manager", "admin_manager"];

const EMPTY_FORM = {
  doctor_id: "",
  visit_date: todayIST(),
  products_discussed: [],
  samples_given: [],
  doctor_feedback: "",
  visit_notes: "",
  follow_up_required: false,
  follow_up_date: "",
  // AD HOC CALL (§2). Off by default; the server decides planned/unplanned
  // from the approved tour plan when it is off. On, a reason is required.
  ad_hoc: false,
  deviation_reason: "",
};

export default function LogVisitPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [params] = useSearchParams();
  const presetDoctor = params.get("doctor");
  // OPENED FROM THE PLAN (§1 Fix B). `?plan_date=` is the planned day whose
  // "Mark visited" button opened this form: it pre-fills the visit date and
  // rides on the payload as `plan_date`. The server still decides
  // `is_planned` / `tour_plan_id` from the approved plan — this is a record
  // of where the officer came from, not a claim the form gets to make.
  const presetPlanDate = (() => {
    const raw = params.get("plan_date");
    return !editId && /^\d{4}-\d{2}-\d{2}$/.test(raw || "") ? raw : null;
  })();
  const isEdit = !!editId;

  const [form, setForm] = useState({
    ...EMPTY_FORM,
    ...(presetPlanDate ? { visit_date: presetPlanDate } : {}),
  });
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoaded, setDoctorsLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  /**
   * THE AD HOC REASONS, SERVED. `settings_deviation_reasons` is Director-
   * editable and `create_visit` validates against the same reader the meta
   * serves them from (#78). `null` until the meta answers; no fallback list —
   * with the switch on and no list, submission is blocked with the error
   * shown, never a guess.
   */
  const [deviationReasons, setDeviationReasons] = useState(null);
  /**
   * OFF-LIST DOCTORS (§2 Session 4). With the ad hoc switch on the picker is
   * drawn from `/doctors/territory-search` — own doctors plus every verified
   * doctor in the officer's territory units — the SAME filter the server
   * applies to an ad hoc filing. `null` until it answers; a failed read is
   * said, and the own-list picker stands in, never a guessed wider list.
   */
  const [territoryDoctors, setTerritoryDoctors] = useState(null);
  const [territoryUnreadable, setTerritoryUnreadable] = useState(false);
  const [doctorQuery, setDoctorQuery] = useState("");
  // QUICK-ADD: the doctor is not in the territory list at all. Three fields,
  // filed in the same request as the visit; the server writes the doctor as
  // pending verification and puts it in the Admin Manager's queue.
  const [quickAdd, setQuickAdd] = useState(false);
  const [newDoctor, setNewDoctor] = useState({ name: "", specialization: "", clinic_area: "" });
  const [specializations, setSpecializations] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | locating | ok | denied | unsupported
  /**
   * THE CAPTURE GATE, read from the backend that enforces it (P3).
   *
   * `null` until it answers, and a failed fetch leaves it null — which this
   * page treats as OFF. There is no fallback constant saying "on": a fallback
   * is used precisely when the fetch failed, which is the worst possible
   * moment to guess that a lawful basis exists. The page never invents the
   * policy wording either; every sentence about capture comes from the payload
   * that the notice and the switch live in.
   */
  const [capture, setCapture] = useState(null);
  const [captureUnreadable, setCaptureUnreadable] = useState(false);
  /**
   * Consent is an ACT, so it starts UNANSWERED every time and is never
   * remembered for the user. Withdrawing is the same tap that gave it.
   *
   * TRI-STATE, matching `routes_field_map.gate_position`: `null` is "nobody
   * has answered", `true` is agreement, `false` is a refusal the officer
   * actually made. Starting at `false` collapsed the first two and made the
   * backend's `consent_not_recorded` branch unreachable from the only real
   * client — every untouched visit was recorded as a refusal the officer never
   * gave. Nothing is captured either way, so the cost is not privacy; it is
   * the record asserting a negative fact nobody stated, which is the rebuild
   * invariant "a gap is shown as a gap".
   */
  const [consent, setConsent] = useState(null);

  /**
   * THE BACKDATING WINDOW, SERVED. `settings_field_policy.dcr_backdate_days`
   * is Director-editable, and `routes_visits.create_visit` validates against
   * it. A `7` frozen here would offer a date the server refuses the moment she
   * narrows it — #78 with a calendar instead of a dropdown.
   *
   * `null` until `/doctor-visits/meta/products` answers, and no fallback: a
   * fallback is used exactly when the fetch failed, which is the worst moment
   * to guess how far back the company allows filing. Unknown means the field
   * is shut and submission is blocked, never "assume seven".
   */
  const [backdateDays, setBackdateDays] = useState(null);
  const [windowUnreadable, setWindowUnreadable] = useState(false);

  const minDate = useMemo(
    () => (backdateDays == null ? undefined : daysAgoIST(backdateDays)),
    [backdateDays],
  );
  const maxDate = useMemo(() => todayIST(), []);

  // Redirect roles that cannot log calls. Both manager tiers log their own
  // DCRs for doctors assigned to them (§1 Fix A, 2026-09-07); Admin and
  // Director file nothing. The list mirrors `routes_visits.VISIT_FILER_ROLES`,
  // which is the gate that actually refuses — this only spares the round trip.
  useEffect(() => {
    if (user && !FILER_ROLES.includes(user.role)) {
      navigate("/doctor-visits", { replace: true });
    }
  }, [user, navigate]);

  // Fetch the product master so the dropdown always matches what the backend
  // validates against (no more "Unknown products" on submit) — and the filing
  // window off the SAME response, which the create handler validates with.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/doctor-visits/meta/products");
        setProducts(data.products || []);
        if (Array.isArray(data.deviation_reasons)) setDeviationReasons(data.deviation_reasons);
        if (typeof data.backdate_days === "number") {
          setBackdateDays(data.backdate_days);
        } else {
          // An older backend mid-deploy serves the products without the
          // window. That is not a seven — it is an unknown, and it is shown
          // as one.
          setWindowUnreadable(true);
        }
      } catch (_) {
        setWindowUnreadable(true);
      }
    })();
  }, []);

  // Is capture switched on at all? Asked BEFORE anything is captured, and the
  // answer decides whether this page is ever allowed to touch `navigator.
  // geolocation` — the browser permission prompt is itself a disclosure, and
  // raising it before the notice is approved is the thing P3 forbids.
  useEffect(() => {
    let cancelled = false;
    api.get("/field-map/capture-state")
      .then(({ data }) => !cancelled && setCapture(data))
      .catch(() => !cancelled && setCaptureUnreadable(true));
    return () => { cancelled = true; };
  }, []);

  // Fetch the caller's OWN doctors for the picker. An officer's unfiltered
  // list is already their own; a manager's is their whole team's (or the
  // company's), and `create_visit` refuses every doctor not assigned to the
  // filer — so the picker narrows with `assigned_to=self`, the same predicate
  // the server checks (`owned_doctor_query` / `doctor_owned_by`). Offering a
  // doctor the server will refuse is #78 with a picker instead of a dropdown.
  const role = user?.role;
  const userId = user?.id;
  useEffect(() => {
    (async () => {
      try {
        const params = role && role !== "sales_officer" && userId ? { assigned_to: userId } : undefined;
        const { data } = await api.get("/doctors", params ? { params } : undefined);
        setDoctors(data);
        setDoctorsLoaded(true);
        if (!isEdit && presetDoctor && data.find((d) => d.id === presetDoctor)) {
          setForm((p) => ({ ...p, doctor_id: presetDoctor }));
        }
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [isEdit, presetDoctor, role, userId]);

  // The territory picker, read once when the switch goes on. Not on edit:
  // the doctor of a filed visit cannot change.
  useEffect(() => {
    if (!form.ad_hoc || isEdit || territoryDoctors !== null) return undefined;
    let cancelled = false;
    api.get("/doctors/territory-search")
      .then(({ data }) => !cancelled && setTerritoryDoctors(data.doctors || []))
      .catch(() => !cancelled && setTerritoryUnreadable(true));
    return () => { cancelled = true; };
  }, [form.ad_hoc, isEdit, territoryDoctors]);

  // The specialisation list, from the same endpoint the doctor form reads —
  // never a copy in this bundle (#78). Read only when quick-add opens.
  useEffect(() => {
    if (!quickAdd || specializations !== null) return undefined;
    let cancelled = false;
    api.get("/doctors/meta/options")
      .then(({ data }) => !cancelled && setSpecializations(data.specializations || []))
      .catch(() => !cancelled && setSpecializations([]));
    return () => { cancelled = true; };
  }, [quickAdd, specializations]);

  // Load the existing visit in edit mode.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/doctor-visits/${editId}`);
        if (cancelled) return;
        setForm({
          doctor_id: data.doctor_id,
          visit_date: data.visit_date,
          products_discussed: data.products_discussed || [],
          samples_given: data.samples_given || [],
          doctor_feedback: data.doctor_feedback || "",
          visit_notes: data.visit_notes || "",
          follow_up_required: !!data.follow_up_required,
          follow_up_date: data.follow_up_date || "",
          // Ad hoc is "unplanned by the officer's word": false + a reason.
          // A server-decided unplanned row (no reason) loads with the switch off.
          ad_hoc: data.is_planned === false && !!data.deviation_reason,
          deviation_reason: data.deviation_reason || "",
        });
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId]);

  // WHICH LIST THE PICKER DRAWS FROM. Ad hoc on and the territory list read:
  // the territory list. Otherwise the own list — including while the
  // territory read is in flight or has failed, which the card says.
  const adHocList = !isEdit && form.ad_hoc && territoryDoctors !== null;
  const pickerDoctors = useMemo(() => {
    const base = adHocList ? territoryDoctors : doctors;
    const q = doctorQuery.trim().toLowerCase();
    if (!adHocList || !q) return base;
    return base.filter((d) =>
      [d.name, d.clinic_hospital_name].some((v) => (v || "").toLowerCase().includes(q)));
  }, [adHocList, territoryDoctors, doctors, doctorQuery]);

  const selectedDoctor = useMemo(
    () => (adHocList ? territoryDoctors : doctors).find((d) => d.id === form.doctor_id) || null,
    [adHocList, territoryDoctors, doctors, form.doctor_id]
  );

  function setAdHoc(on) {
    update("ad_hoc", on);
    if (!on) {
      // Off again: quick-add needs the switch, and a colleague's doctor
      // picked from the territory list is not filable on the planned path.
      setQuickAdd(false);
      setDoctorQuery("");
      if (selectedDoctor && selectedDoctor.mine === false) update("doctor_id", "");
    }
  }

  function openQuickAdd() {
    setQuickAdd(true);
    update("doctor_id", "");
  }

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleProduct(p) {
    setForm((prev) => {
      const set = new Set(prev.products_discussed);
      if (set.has(p)) set.delete(p);
      else set.add(p);
      return { ...prev, products_discussed: Array.from(set) };
    });
  }

  function addSample() {
    if (!products.length) return;
    setForm((p) => ({
      ...p,
      samples_given: [...p.samples_given, { product: products[0], quantity: 1 }],
    }));
  }

  function updateSample(idx, key, value) {
    setForm((p) => ({
      ...p,
      samples_given: p.samples_given.map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    }));
  }

  function removeSample(idx) {
    setForm((p) => ({
      ...p,
      samples_given: p.samples_given.filter((_, i) => i !== idx),
    }));
  }

  const captureOn = !!capture?.enabled;
  const reasonLabels = capture?.not_captured_reason_labels || {};

  /**
   * The one-shot read, at the moment of the event. There is no watcher and no
   * timer here on purpose — `setTimeout` below gives up on a slow fix, it does
   * not schedule a second read (`test_field_tracking_scope.py`).
   */
  function captureGps() {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        setGpsStatus("unsupported");
        return resolve(null);
      }
      setGpsStatus("locating");
      const timer = setTimeout(() => {
        setGpsStatus("denied");
        resolve(null);
      }, 4500);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timer);
          setGpsStatus("ok");
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          clearTimeout(timer);
          setGpsStatus("denied");
          resolve(null);
        },
        { timeout: 4000, enableHighAccuracy: false }
      );
    });
  }

  function validate() {
    if (quickAdd) {
      if (!newDoctor.name.trim()) return "Please enter the new doctor's name.";
      if (!newDoctor.specialization) return "Please pick the new doctor's specialisation.";
      if (!newDoctor.clinic_area) return "Please pick the new doctor's clinic area.";
    } else if (!form.doctor_id) return "Please pick a doctor.";
    if (!form.visit_date) return "Please pick the visit date.";
    // Not a guess in either direction. Without the window this form cannot say
    // whether the chosen date is filable, and submitting to find out puts the
    // server's refusal in front of the officer instead of the answer.
    if (!isEdit && backdateDays == null) {
      return "The filing window could not be read from the server. Reload the page before filing.";
    }
    if (!form.doctor_feedback) return "Please record the doctor's feedback.";
    if (form.follow_up_required && !form.follow_up_date)
      return "Please set the follow-up date.";
    if (form.ad_hoc) {
      if (deviationReasons == null)
        return "The ad hoc reasons could not be read from the server. Reload the page before filing.";
      if (!form.deviation_reason) return "Please pick the reason for this ad hoc call.";
    }
    for (const s of form.samples_given) {
      const q = Number(s.quantity);
      if (!Number.isFinite(q) || q <= 0) return "Sample quantity must be a positive number.";
    }
    return null;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);

    // CONSENT GATES CAPTURE, ON THIS SIDE TOO. The backend drops anything the
    // gate refuses, but the browser must not even be ASKED for a position
    // without both the switch and the officer's agreement — a permission
    // prompt raised on an unapproved notice cannot be taken back.
    let gps = null;
    if (!isEdit && captureOn && consent === true) {
      gps = await captureGps();
    }

    try {
      const payload = {
        doctor_id: quickAdd ? "" : form.doctor_id,
        // Only when the form drew it (D-036): the three quick-add fields
        // travel with the switch on and the sub-form open, else nothing.
        ...(quickAdd && form.ad_hoc
          ? { new_doctor: { name: newDoctor.name.trim(), specialization: newDoctor.specialization,
                            clinic_area: newDoctor.clinic_area } }
          : {}),
        visit_date: form.visit_date,
        products_discussed: form.products_discussed,
        samples_given: form.samples_given.map((s) => ({
          product: s.product,
          quantity: Number(s.quantity),
        })),
        doctor_feedback: form.doctor_feedback,
        visit_notes: form.visit_notes.trim() || null,
        follow_up_required: form.follow_up_required,
        follow_up_date: form.follow_up_required ? form.follow_up_date : null,
        // Only what the form draws is sent (D-036): the reason travels with the
        // switch on, and is null with it off — the server ignores it then.
        ad_hoc: form.ad_hoc,
        deviation_reason: form.ad_hoc ? form.deviation_reason : null,
        // The planned day this form was opened from, or nothing (D-036: a
        // field the form did not receive is not sent as a value).
        plan_date: presetPlanDate,
        gps_location: gps,
        // Tri-state, matching the backend: the boolean is an answer the
        // officer gave; `null` is "nobody was asked", which is not a refusal
        // and is certainly not agreement.
        location_consent: captureOn ? consent : null,
      };
      const { data } = isEdit
        ? await api.put(`/doctor-visits/${editId}`, payload)
        : await api.post("/doctor-visits", payload);
      toast.success(
        isEdit ? "Visit updated"
          : data.new_doctor ? "Visit logged · new doctor sent for verification"
          : "Visit logged successfully");
      navigate(`/doctor-visits/${data.id}`, { replace: true, state: { justLogged: !isEdit } });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-callisto-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading visit…
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="log-visit-page">
      <Link
        to="/doctor-visits"
        className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy"
      >
        <ArrowLeft size={14} />
        Back
      </Link>
      <div className="mt-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
          {isEdit ? "Edit visit" : "Daily Call Report"}
        </span>
        <h1 className="mt-1 text-2xl md:text-3xl font-bold text-callisto-navy">
          {isEdit ? "Edit visit" : "Log a doctor visit"}
        </h1>
        {/* The old subtitle promised "your GPS is auto-attached when your
            phone allows it" — which was true, and was exactly the behaviour
            that had no consent behind it. What happens to your location is now
            said once, by the card at the bottom, in the words the backend
            serves alongside the switch that decides it. */}
        <p className="mt-1 text-sm text-callisto-muted">
          Capture the day's call in under a minute.
        </p>
      </div>

      {/* RC-16 (F242/F276): the create surface says WHY it cannot be used,
          instead of offering a form whose first required field has no options.
          Only after the roster actually LOADED — an empty list mid-load or
          after a failed fetch is unknown, not "no doctors" (Pattern A). */}
      {doctorsLoaded && doctors.length === 0 && !isEdit ? (
        <div
          className="mt-6 bg-white border border-callisto rounded-xl p-8 text-center"
          data-testid="visit-no-doctors"
        >
          <p className="text-sm font-semibold text-callisto-navy">
            No doctors are assigned to you yet.
          </p>
          <p className="mt-1 text-xs text-callisto-muted">
            A visit is logged against one of your own doctors. Ask your manager
            to assign your book, then come back here.
          </p>
        </div>
      ) : (
      <form onSubmit={onSubmit} className="mt-6 space-y-5">
        {/* Doctor + date */}
        <Card title="Doctor & date">
          <div>
            <Label htmlFor="doctor" className="text-callisto-navy font-medium">
              Doctor <span className="text-callisto-gold">*</span>
            </Label>
            {/* OFF-LIST (§2 Session 4): with the switch on, the picker widens
                to the officer's territory units and gains a search box. */}
            {!isEdit && form.ad_hoc && (
              <div className="mt-1.5 space-y-1.5" data-testid="visit-adhoc-picker">
                <Input
                  value={doctorQuery}
                  onChange={(e) => setDoctorQuery(e.target.value)}
                  placeholder="Search doctors in your territory"
                  className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid="visit-input-doctor-search"
                  disabled={quickAdd}
                />
                {territoryUnreadable ? (
                  <p className="text-xs text-red-700" data-testid="visit-territory-unreadable">
                    Doctors in your territory could not be read. Only your own list is shown.
                  </p>
                ) : territoryDoctors === null ? (
                  <p className="text-xs text-callisto-muted">Reading doctors in your territory…</p>
                ) : (
                  <p className="text-xs text-callisto-muted">
                    Showing your doctors and every verified doctor in your territory units.
                  </p>
                )}
              </div>
            )}
            {!quickAdd && (
            <Select
              value={form.doctor_id}
              onValueChange={(v) => update("doctor_id", v)}
              disabled={isEdit}
            >
              <SelectTrigger
                id="doctor"
                className="h-12 mt-1.5 focus:ring-callisto-gold"
                data-testid="visit-input-doctor"
              >
                <SelectValue placeholder={pickerDoctors.length ? "Select doctor" : (doctors.length ? "No doctor matches" : "Loading…")} />
              </SelectTrigger>
              <SelectContent>
                {pickerDoctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} — {d.specialization}{d.category ? ` (${d.category})` : ""}
                    {adHocList && d.mine === false && d.assigned_to_user?.name
                      ? ` · ${d.assigned_to_user.name}'s list` : ""}
                    {adHocList && d.pending ? " · awaiting verification" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            {!isEdit && form.ad_hoc && !quickAdd && (
              <button
                type="button"
                onClick={openQuickAdd}
                className="mt-2 text-xs font-semibold text-callisto-navy underline underline-offset-2"
                data-testid="visit-quick-add-open"
              >
                Doctor not found? Add new doctor
              </button>
            )}
            {quickAdd && form.ad_hoc && (
              <div className="mt-2 space-y-3 rounded-lg border border-callisto bg-callisto-navy/5 p-3" data-testid="visit-quick-add">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-callisto-muted">
                    New doctor. Saved with this visit and sent to the Admin Manager for
                    verification; until then only you can see them.
                  </p>
                  <button
                    type="button"
                    onClick={() => setQuickAdd(false)}
                    className="text-xs font-semibold text-callisto-navy underline underline-offset-2 shrink-0"
                    data-testid="visit-quick-add-cancel"
                  >
                    Pick from list
                  </button>
                </div>
                <div>
                  <Label htmlFor="nd-name" className="text-callisto-navy font-medium">Name <span className="text-callisto-gold">*</span></Label>
                  <Input
                    id="nd-name"
                    value={newDoctor.name}
                    onChange={(e) => setNewDoctor((p) => ({ ...p, name: e.target.value }))}
                    className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                    data-testid="visit-input-new-doctor-name"
                  />
                </div>
                <div>
                  <Label className="text-callisto-navy font-medium">Specialisation <span className="text-callisto-gold">*</span></Label>
                  {specializations === null ? (
                    <p className="mt-1.5 text-xs text-callisto-muted">Loading…</p>
                  ) : specializations.length === 0 ? (
                    <p className="mt-1.5 text-xs text-red-700" data-testid="visit-specializations-unreadable">
                      The specialisation list could not be read from the server. Reload the page before filing.
                    </p>
                  ) : (
                    <Select
                      value={newDoctor.specialization}
                      onValueChange={(v) => setNewDoctor((p) => ({ ...p, specialization: v }))}
                    >
                      <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="visit-input-new-doctor-specialization">
                        <SelectValue placeholder="Select specialisation" />
                      </SelectTrigger>
                      <SelectContent>
                        {specializations.map((sp) => (
                          <SelectItem key={sp} value={sp}>{sp}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-callisto-navy font-medium">Clinic area <span className="text-callisto-gold">*</span></Label>
                  <div className="mt-1.5">
                    <TerritoryUnitSelect
                      value={newDoctor.clinic_area}
                      onChange={(v) => setNewDoctor((p) => ({ ...p, clinic_area: v }))}
                      testId="visit-input-new-doctor-area"
                    />
                  </div>
                </div>
              </div>
            )}
            {selectedDoctor && adHocList && selectedDoctor.mine === false && (
              <p className="mt-2 text-xs text-sky-900" data-testid="visit-other-officer-note">
                On {selectedDoctor.assigned_to_user?.name || "another officer"}'s list. This visit
                will be marked as another officer's doctor for your manager.
              </p>
            )}
            {selectedDoctor && (
              <div
                className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-callisto-muted"
                data-testid="visit-doctor-summary"
              >
                <CategoryBadge category={selectedDoctor.category} size="sm" />
                {/* RC-10: the separators were literal `·` spans, so a doctor
                    with no clinic or no area rendered a dangling "· ·". A
                    separator belongs between things that exist. */}
                <span className="truncate">
                  {joinParts([
                    selectedDoctor.clinic_hospital_name,
                    selectedDoctor.territory_unit || selectedDoctor.area,
                  ])}
                </span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="vdate" className="text-callisto-navy font-medium">
              Visit date <span className="text-callisto-gold">*</span>
            </Label>
            <Input
              id="vdate"
              type="date"
              min={minDate}
              max={maxDate}
              value={form.visit_date}
              onChange={(e) => update("visit_date", e.target.value)}
              className="h-12 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="visit-input-date"
              disabled={isEdit || (!isEdit && backdateDays == null)}
            />
            {/* The sentence is built from the served number, so the copy and
                the `min` attribute cannot describe different windows. */}
            <p className="mt-1 text-[11px] text-callisto-muted" data-testid="visit-date-window">
              {backdateDays != null
                ? `Backdate up to ${backdateDays} ${backdateDays === 1 ? "day" : "days"}. Today is the default.`
                : windowUnreadable
                  ? "The filing window could not be read from the server, so this date cannot be set. Reload the page."
                  : "Checking how far back filing is allowed…"}
            </p>
          </div>
        </Card>

        {/* Products discussed */}
        <Card title="Products discussed">
          <div className="grid grid-cols-2 gap-2" data-testid="visit-products-grid">
            {products.map((p) => {
              const checked = form.products_discussed.includes(p);
              return (
                <label
                  key={p}
                  htmlFor={`prod-${p}`}
                  className={`flex items-center gap-2 rounded-lg border p-3 text-sm cursor-pointer transition-colors min-h-[48px] ${
                    checked
                      ? "border-callisto-gold bg-callisto-gold/5"
                      : "border-callisto hover:border-callisto-navy/30"
                  }`}
                >
                  <Checkbox
                    id={`prod-${p}`}
                    checked={checked}
                    onCheckedChange={() => toggleProduct(p)}
                    data-testid={`visit-product-${p.replace(/\s+/g, "-")}`}
                  />
                  <span
                    className={`font-medium ${
                      checked ? "text-callisto-navy" : "text-callisto-body"
                    }`}
                  >
                    {p}
                  </span>
                </label>
              );
            })}
          </div>
        </Card>

        {/* Samples given */}
        <Card title="Samples given" subtitle="optional">
          {form.samples_given.length === 0 ? (
            <p className="text-sm text-callisto-muted">No samples handed out yet.</p>
          ) : (
            <ul className="space-y-2">
              {form.samples_given.map((s, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-2 bg-callisto-app rounded-lg p-2"
                >
                  <Select
                    value={s.product}
                    onValueChange={(v) => updateSample(idx, "product", v)}
                  >
                    <SelectTrigger className="h-10 flex-1 bg-white focus:ring-callisto-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min={1}
                    max={999}
                    value={s.quantity}
                    onChange={(e) => updateSample(idx, "quantity", e.target.value)}
                    className="h-10 w-20 bg-white"
                    data-testid={`visit-sample-qty-${idx}`}
                  />
                  <button
                    type="button"
                    aria-label="Remove sample"
                    onClick={() => removeSample(idx)}
                    className="h-10 w-10 rounded-md text-red-600 hover:bg-red-50 flex items-center justify-center"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={addSample}
            className="mt-3 h-10 border-callisto"
            data-testid="visit-add-sample"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add sample
          </Button>
        </Card>

        {/* Feedback + notes */}
        <Card title="Doctor feedback">
          <div>
            <Label className="text-callisto-navy font-medium">
              Feedback <span className="text-callisto-gold">*</span>
            </Label>
            <Select
              value={form.doctor_feedback}
              onValueChange={(v) => update("doctor_feedback", v)}
            >
              <SelectTrigger
                className="h-12 mt-1.5 focus:ring-callisto-gold"
                data-testid="visit-input-feedback"
              >
                <SelectValue placeholder="Select feedback" />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_OPTIONS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="vnotes" className="text-callisto-navy font-medium">
              Visit notes <span className="text-callisto-muted font-normal">(optional)</span>
            </Label>
            <Textarea
              id="vnotes"
              value={form.visit_notes}
              onChange={(e) => update("visit_notes", e.target.value)}
              rows={3}
              placeholder="Anything worth remembering — concerns raised, prescription habits, competitors mentioned…"
              className="mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="visit-input-notes"
            />
          </div>
        </Card>

        {/* Follow-up */}
        {/* AD HOC CALL (§2). Off: the server checks the approved tour plan and
            records planned or not — the officer is never blocked for a call
            that was not in the plan. On: a reason is required, from the
            Director-editable list the server also validates against. */}
        <Card title="Ad hoc call">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-callisto-navy">Was this call unplanned?</p>
              <p className="text-xs text-callisto-muted">
                Switch on if the doctor was not in your tour plan for this day.
              </p>
            </div>
            <Switch
              checked={form.ad_hoc}
              onCheckedChange={setAdHoc}
              data-testid="visit-input-adhoc-toggle"
            />
          </div>
          {form.ad_hoc && (
            <div>
              <Label className="text-callisto-navy font-medium">Reason</Label>
              {deviationReasons == null ? (
                <p className="mt-1.5 text-xs text-red-700" data-testid="visit-adhoc-reasons-unreadable">
                  The reasons could not be read from the server. Reload the page before filing.
                </p>
              ) : (
                <Select
                  value={form.deviation_reason}
                  onValueChange={(v) => update("deviation_reason", v)}
                >
                  <SelectTrigger
                    className="h-12 mt-1.5 focus:ring-callisto-gold"
                    data-testid="visit-input-adhoc-reason"
                  >
                    <SelectValue placeholder="Why was this call unplanned?" />
                  </SelectTrigger>
                  <SelectContent>
                    {deviationReasons.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </Card>

        <Card title="Follow-up">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-callisto-navy">
                Follow-up required?
              </p>
              <p className="text-xs text-callisto-muted">
                Schedule the next visit while it's fresh.
              </p>
            </div>
            <Switch
              checked={form.follow_up_required}
              onCheckedChange={(v) => update("follow_up_required", v)}
              data-testid="visit-input-followup-toggle"
            />
          </div>
          {form.follow_up_required && (
            <div>
              <Label htmlFor="fdate" className="text-callisto-navy font-medium">
                Follow-up date
              </Label>
              <Input
                id="fdate"
                type="date"
                min={todayIST()}
                value={form.follow_up_date}
                onChange={(e) => update("follow_up_date", e.target.value)}
                className="h-12 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="visit-input-followup-date"
              />
            </div>
          )}
        </Card>

        {/* LOCATION — the switch, the ask, and the closed state said out loud.
            An unapproved consent notice must not look like a feature that
            merely happens to be quiet: a Sales Officer reading this page can
            see whether anything is captured and why not. */}
        {!isEdit && (
          <Card title="Location">
            {captureOn ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-callisto-navy">
                      {capture.consent_prompt}
                    </p>
                    <p className="text-xs text-callisto-muted">{capture.consent_detail}</p>
                  </div>
                  <Switch
                    checked={consent === true}
                    onCheckedChange={(v) => setConsent(!!v)}
                    data-testid="visit-location-consent"
                    aria-label={capture.consent_prompt}
                  />
                </div>
                <p
                  className="text-[11px] text-callisto-muted flex items-start gap-1.5"
                  data-testid="visit-location-status"
                >
                  <MapPin size={12} className="shrink-0 mt-0.5" />
                  <span>
                    {consent !== true
                      // A refusal is an answer and an unanswered prompt is not,
                      // and they read differently. The visit logs either way.
                      ? `${NOT_CAPTURED} ${(consent === false
                          ? reasonLabels.consent_withheld
                          : reasonLabels.consent_not_recorded) || ""}`.trim()
                      : gpsStatus === "locating"
                      ? "Locating you…"
                      : gpsStatus === "ok"
                      ? "Location captured for this visit."
                      : gpsStatus === "denied" || gpsStatus === "unsupported"
                      ? `${NOT_CAPTURED} ${reasonLabels.device_unavailable || ""}`.trim()
                      : "Your location will be read once, when you submit."}
                  </span>
                </p>
              </>
            ) : capture ? (
              <div data-testid="visit-capture-off" className="text-sm">
                <p className="font-semibold text-callisto-navy">{capture.headline}</p>
                <p className="mt-1 text-xs text-callisto-muted">{capture.detail}</p>
              </div>
            ) : captureUnreadable ? (
              // About the FETCH, not about the policy — the page still refuses
              // to capture, and refuses just as hard to invent the notice.
              <p className="text-xs text-callisto-muted" data-testid="visit-capture-unknown">
                Whether location capture is switched on could not be checked, so
                nothing will be captured with this visit. The visit itself saves
                normally.
              </p>
            ) : (
              <p className="text-xs text-callisto-muted">Checking…</p>
            )}
          </Card>
        )}

        {error && (
          <div
            data-testid="visit-error"
            className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="sticky bottom-3 bg-transparent">
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold shadow-lg"
            data-testid="visit-submit-button"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isEdit ? (
              <>
                <Save className="h-4 w-4 mr-1.5" />
                Save changes
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                Submit visit
              </>
            )}
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <section className="bg-white border border-callisto rounded-xl p-5 space-y-4">
      <header className="flex items-baseline gap-2">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
          {title}
        </h2>
        {subtitle && (
          <span className="text-[11px] text-callisto-muted lowercase">{subtitle}</span>
        )}
      </header>
      {children}
    </section>
  );
}
